import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../contexts/AppContext';
import { Target, ArrowLeft, Save, AlertCircle, Clock, Check, X } from 'lucide-react';
import clsx from 'clsx';
import { ProjectScoping } from '../../types';
import { NextStepButton } from '../../components/WorkflowNavigation';
import { loadProjectScoping, saveProjectScoping, ProjectScopingData } from '../../services/projectService';

import { ScopingStats } from './ScopingStats';
import { IntentSection } from './IntentSection';
import { TimingSection } from './TimingSection';
import { TeamSection } from './TeamSection';
import { ToolsSection } from './ToolsSection';
import { TasksSection } from './TasksSection';

// Modal types for different edit forms
type ModalType = 'intent' | 'timing' | 'team' | 'tools' | 'task' | 'milestone' | null;

interface TaskForm {
    id: string;
    name: string;
    owner: string;
    dueDate: string;
    status: 'not-started' | 'in-progress' | 'completed' | 'blocked';
}

interface TeamMemberForm {
    name: string;
    role: string;
    department: string;
    expertise: string;
}

interface MilestoneForm {
    name: string;
    date: string;
    status: 'pending' | 'in-progress' | 'completed';
}

const ProjectScopingPage: React.FC = () => {
    const { activeProject, currentUser, theme } = useAppContext();
    const navigate = useNavigate();
    const isDark = theme === 'dark';

    const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'autoGainAdmin' || currentUser?.role === 'engineer';

    // Modal state
    const [activeModal, setActiveModal] = useState<ModalType>(null);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [saveMessage, setSaveMessage] = useState<string | null>(null);

    // Form states
    const [taskForm, setTaskForm] = useState<TaskForm>({
        id: '',
        name: '',
        owner: '',
        dueDate: '',
        status: 'not-started',
    });
    const [teamMemberForm, setTeamMemberForm] = useState<TeamMemberForm>({
        name: '',
        role: '',
        department: '',
        expertise: '',
    });
    const [milestoneForm, setMilestoneForm] = useState<MilestoneForm>({
        name: '',
        date: '',
        status: 'pending',
    });

    // Intent form
    const [intentForm, setIntentForm] = useState({
        goals: '',
        scopeBoundaries: '',
        assumptions: '',
        exclusions: '',
    });

    // Tools form
    const [toolsForm, setToolsForm] = useState({
        software: '',
        templates: '',
        standards: '',
        references: '',
    });

    // Default initial state (empty/template)
    const initialScopingState: ProjectScoping = {
        intent: {
            goals: ['Identify all potential failure modes', 'Achieve zero customer returns', 'Obtain IATF 16949 certification'],
            scopeBoundaries: 'Assembly line (OP-010 through OP-050)',
            assumptions: ['Raw material meets standards', 'Operators are trained'],
            exclusions: ['Post-processing (handled by separate team)'],
        },
        timing: {
            kickoffDate: new Date(),
            targetCompletionDate: new Date(new Date().setMonth(new Date().getMonth() + 3)),
            milestones: [
                { name: 'Structure Analysis Complete', date: new Date(new Date().setDate(new Date().getDate() + 14)), status: 'pending' },
                { name: 'Risk Analysis Complete', date: new Date(new Date().setMonth(new Date().getMonth() + 2)), status: 'pending' },
            ],
        },
        team: {
            lead: 'Quality Manager',
            members: [],
        },
        tools: {
            software: ['Magnus PFMEA Platform'],
            templates: ['AIAG-VDA PFMEA Template', 'Control Plan Template'],
            standards: ['AIAG-VDA FMEA Handbook (2019)'],
            references: [],
        },
        tasks: [],
    };

    const [scoping, setScoping] = useState<ProjectScoping>(initialScopingState);
    const [isLoading, setIsLoading] = useState(false);

    // Load data from Firebase
    React.useEffect(() => {
        if (activeProject) {
            loadData();
        }
    }, [activeProject]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const data = await loadProjectScoping(activeProject!);
            if (data) {
                // Convert string dates back to Date objects
                const parsedData: ProjectScoping = {
                    ...data,
                    timing: {
                        ...data.timing,
                        kickoffDate: new Date(data.timing.kickoffDate),
                        targetCompletionDate: new Date(data.timing.targetCompletionDate),
                        milestones: data.timing.milestones.map(m => ({
                            ...m,
                            date: new Date(m.date)
                        }))
                    },
                    tasks: data.tasks.map(t => ({
                        ...t,
                        dueDate: new Date(t.dueDate)
                    }))
                };
                setScoping(parsedData);
            }
        } catch (error) {
            console.error('Failed to load project scoping:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // ============================================================
    // UTILITY FUNCTIONS
    // ============================================================

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
            case 'in-progress': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
            case 'pending':
            case 'not-started': return 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400';
            case 'blocked': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
            default: return 'bg-slate-100 text-slate-600';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'completed': return <Check size={14} />;
            case 'in-progress': return <Clock size={14} />;
            case 'blocked': return <X size={14} />;
            default: return null;
        }
    };

    const formatDateForInput = (date: Date): string => date.toISOString().split('T')[0];

    // ============================================================
    // SAVE HANDLER
    // ============================================================

    const handleSave = async () => {
        if (!activeProject) return;

        setSaveMessage('Saving...');
        try {
            const dataToSave: ProjectScopingData = {
                ...scoping,
                timing: {
                    ...scoping.timing,
                    kickoffDate: scoping.timing.kickoffDate.toISOString(),
                    targetCompletionDate: scoping.timing.targetCompletionDate.toISOString(),
                    milestones: scoping.timing.milestones.map(m => ({
                        ...m,
                        date: m.date.toISOString()
                    }))
                },
                tasks: scoping.tasks.map(t => ({
                    ...t,
                    dueDate: t.dueDate.toISOString()
                }))
            };

            const success = await saveProjectScoping(activeProject, dataToSave);
            if (success) {
                setSaveMessage('Project scoping saved successfully!');
            } else {
                setSaveMessage('Error saving project scoping');
            }
        } catch (error) {
            console.error('Save error:', error);
            setSaveMessage('Error saving project scoping');
        }
        setTimeout(() => setSaveMessage(null), 3000);
    };

    // ============================================================
    // INTENT HANDLERS
    // ============================================================

    const openIntentModal = () => {
        setIntentForm({
            goals: scoping.intent.goals.join('\n'),
            scopeBoundaries: scoping.intent.scopeBoundaries,
            assumptions: scoping.intent.assumptions.join('\n'),
            exclusions: scoping.intent.exclusions?.join('\n') || '',
        });
        setActiveModal('intent');
    };

    const saveIntent = () => {
        setScoping(prev => ({
            ...prev,
            intent: {
                goals: intentForm.goals.split('\n').filter(g => g.trim()),
                scopeBoundaries: intentForm.scopeBoundaries,
                assumptions: intentForm.assumptions.split('\n').filter(a => a.trim()),
                exclusions: intentForm.exclusions.split('\n').filter(e => e.trim()),
            },
        }));
        setActiveModal(null);
    };

    // ============================================================
    // TIMING/MILESTONE HANDLERS
    // ============================================================

    const openTimingModal = () => setActiveModal('timing');

    const openMilestoneModal = (index?: number) => {
        if (index !== undefined) {
            const milestone = scoping.timing.milestones[index];
            setMilestoneForm({ name: milestone.name, date: formatDateForInput(milestone.date), status: milestone.status });
            setEditingIndex(index);
        } else {
            setMilestoneForm({ name: '', date: '', status: 'pending' });
            setEditingIndex(null);
        }
        setActiveModal('milestone');
    };

    const saveMilestone = () => {
        const newMilestone = { name: milestoneForm.name, date: new Date(milestoneForm.date), status: milestoneForm.status };
        setScoping(prev => {
            const milestones = [...prev.timing.milestones];
            if (editingIndex !== null) milestones[editingIndex] = newMilestone;
            else milestones.push(newMilestone);
            return { ...prev, timing: { ...prev.timing, milestones } };
        });
        setActiveModal(null);
        setEditingIndex(null);
    };

    const deleteMilestone = (index: number) => {
        if (confirm('Are you sure you want to delete this milestone?')) {
            setScoping(prev => ({
                ...prev, timing: { ...prev.timing, milestones: prev.timing.milestones.filter((_, i) => i !== index) },
            }));
        }
    };

    const updateTimingDates = (kickoff: string, completion: string) => {
        setScoping(prev => ({ ...prev, timing: { ...prev.timing, kickoffDate: new Date(kickoff), targetCompletionDate: new Date(completion) } }));
    };

    // ============================================================
    // TEAM HANDLERS
    // ============================================================

    const openTeamModal = (index?: number) => {
        if (index !== undefined) {
            const member = scoping.team.members[index];
            setTeamMemberForm({ ...member });
            setEditingIndex(index);
        } else {
            setTeamMemberForm({ name: '', role: '', department: '', expertise: '' });
            setEditingIndex(null);
        }
        setActiveModal('team');
    };

    const saveTeamMember = () => {
        setScoping(prev => {
            const members = [...prev.team.members];
            if (editingIndex !== null) members[editingIndex] = { ...teamMemberForm };
            else members.push({ ...teamMemberForm });
            return { ...prev, team: { ...prev.team, members } };
        });
        setActiveModal(null);
        setEditingIndex(null);
    };

    const deleteTeamMember = (index: number) => {
        if (confirm('Are you sure you want to remove this team member?')) {
            setScoping(prev => ({ ...prev, team: { ...prev.team, members: prev.team.members.filter((_, i) => i !== index) } }));
        }
    };

    // ============================================================
    // TOOLS HANDLERS
    // ============================================================

    const openToolsModal = () => {
        setToolsForm({
            software: scoping.tools.software.join('\n'),
            templates: scoping.tools.templates.join('\n'),
            standards: scoping.tools.standards.join('\n'),
            references: scoping.tools.references.join('\n'),
        });
        setActiveModal('tools');
    };

    const saveTools = () => {
        setScoping(prev => ({
            ...prev,
            tools: {
                software: toolsForm.software.split('\n').filter(s => s.trim()),
                templates: toolsForm.templates.split('\n').filter(t => t.trim()),
                standards: toolsForm.standards.split('\n').filter(s => s.trim()),
                references: toolsForm.references.split('\n').filter(r => r.trim()),
            },
        }));
        setActiveModal(null);
    };

    // ============================================================
    // TASK HANDLERS
    // ============================================================

    const openTaskModal = (index?: number) => {
        if (index !== undefined) {
            const task = scoping.tasks[index];
            setTaskForm({ id: task.id, name: task.name, owner: task.owner, dueDate: formatDateForInput(task.dueDate), status: task.status });
            setEditingIndex(index);
        } else {
            setTaskForm({ id: `task-${Date.now()}`, name: '', owner: '', dueDate: '', status: 'not-started' });
            setEditingIndex(null);
        }
        setActiveModal('task');
    };

    const saveTask = () => {
        const newTask = { id: taskForm.id, name: taskForm.name, owner: taskForm.owner, dueDate: new Date(taskForm.dueDate), status: taskForm.status };
        setScoping(prev => {
            const tasks = [...prev.tasks];
            if (editingIndex !== null) tasks[editingIndex] = { ...tasks[editingIndex], ...newTask };
            else tasks.push(newTask);
            return { ...prev, tasks };
        });
        setActiveModal(null);
        setEditingIndex(null);
    };

    const deleteTask = (index: number) => {
        if (confirm('Are you sure you want to delete this task?')) {
            setScoping(prev => ({ ...prev, tasks: prev.tasks.filter((_, i) => i !== index) }));
        }
    };

    const toggleTaskStatus = (index: number) => {
        const statusOrder: Array<'not-started' | 'in-progress' | 'completed' | 'blocked'> = ['not-started', 'in-progress', 'completed'];
        setScoping(prev => {
            const tasks = [...prev.tasks];
            const currentStatus = tasks[index].status;
            const currentIdx = statusOrder.indexOf(currentStatus);
            const nextStatus = statusOrder[(currentIdx + 1) % statusOrder.length];
            tasks[index] = { ...tasks[index], status: nextStatus };
            return { ...prev, tasks };
        });
    };

    // ============================================================
    // COMPUTED VALUES
    // ============================================================

    const completedTasks = scoping.tasks.filter(t => t.status === 'completed').length;
    const completedMilestones = scoping.timing.milestones.filter(m => m.status === 'completed').length;

    // ============================================================
    // MODAL COMPONENT
    // ============================================================

    const Modal: React.FC<{ title: string; onClose: () => void; onSave: () => void; children: React.ReactNode }> = ({
        title, onClose, onSave, children,
    }) => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className={clsx('w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg shadow-xl', isDark ? 'bg-slate-800' : 'bg-white')}>
                <div className={clsx('flex items-center justify-between p-4 border-b', isDark ? 'border-slate-700' : 'border-slate-200')}>
                    <h2 className={clsx('text-lg font-semibold', isDark ? 'text-white' : 'text-slate-800')}>{title}</h2>
                    <button onClick={onClose} className="p-1 hover:bg-slate-600 rounded">
                        <X size={20} className="text-slate-400" />
                    </button>
                </div>
                <div className="p-4 space-y-4">{children}</div>
                <div className={clsx('flex justify-end gap-2 p-4 border-t', isDark ? 'border-slate-700' : 'border-slate-200')}>
                    <button onClick={onClose} className={clsx('px-4 py-2 rounded text-sm', isDark ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-slate-100 hover:bg-slate-200')}>Cancel</button>
                    <button onClick={onSave} className="px-4 py-2 bg-primary-500 text-white rounded text-sm hover:bg-primary-600">Save</button>
                </div>
            </div>
        </div>
    );

    const inputClasses = clsx(
        'w-full px-3 py-2 rounded border text-sm',
        isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400'
    );

    const labelClasses = clsx('block text-sm font-medium mb-1', isDark ? 'text-slate-300' : 'text-slate-700');

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className={clsx('h-full flex flex-col', isDark ? 'bg-slate-900' : 'bg-slate-50')}>
            {/* Header */}
            <header className={clsx('h-14 border-b flex items-center justify-between px-4 shrink-0', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/')} className="p-1.5 hover:bg-slate-700 rounded"><ArrowLeft size={18} /></button>
                    <Target size={20} className="text-purple-500" />
                    <div>
                        <h1 className={clsx('text-lg font-bold', isDark ? 'text-white' : 'text-slate-800')}>5T Project Scoping</h1>
                        <p className="text-xs text-slate-500">AIAG-VDA Step 1: Planning & Preparation</p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {isLoading && <span className="text-sm text-slate-500 animate-pulse">Loading...</span>}
                    {saveMessage && <span className={clsx("text-sm mr-2", saveMessage.includes('Error') ? 'text-red-500' : 'text-green-500')}>{saveMessage}</span>}
                    <span className="text-sm text-slate-500">Project: <span className="font-medium">{activeProject || 'Unnamed'}</span></span>
                    {canEdit && (
                        <button onClick={handleSave} disabled={isLoading} className="flex items-center gap-2 px-3 py-1.5 bg-primary-500 text-white rounded text-sm hover:bg-primary-600 disabled:opacity-50">
                            <Save size={16} /> Save
                        </button>
                    )}
                </div>
            </header>

            {/* Info Banner */}
            <div className={clsx('mx-4 mt-4 p-3 rounded-lg border flex items-start gap-3', isDark ? 'bg-purple-900/20 border-purple-700/50' : 'bg-purple-50 border-purple-200')}>
                <AlertCircle size={18} className="text-purple-500 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                    <p className={clsx('font-medium mb-1', isDark ? 'text-purple-300' : 'text-purple-900')}>5T Framework (AIAG-VDA)</p>
                    <p className={isDark ? 'text-purple-200/80' : 'text-purple-800/80'}>Define Intent, Timing, Team, Tools, and Tasks before beginning FMEA analysis. This ensures proper planning and preparation.</p>
                </div>
            </div>

            <ScopingStats isDark={isDark} completedTasks={completedTasks} totalTasks={scoping.tasks.length} completedMilestones={completedMilestones} totalMilestones={scoping.timing.milestones.length} teamSize={scoping.team.members.length} goalCount={scoping.intent.goals.length} />

            {/* 5T Sections */}
            <div className="flex-1 overflow-auto px-4 pb-4 space-y-4">
                <IntentSection intent={scoping.intent} isDark={isDark} canEdit={canEdit} onEdit={openIntentModal} />
                <TimingSection timing={scoping.timing} isDark={isDark} canEdit={canEdit} onEdit={openTimingModal} onAddMilestone={() => openMilestoneModal()} onEditMilestone={openMilestoneModal} onDeleteMilestone={deleteMilestone} getStatusColor={getStatusColor} getStatusIcon={getStatusIcon} />
                <TeamSection team={scoping.team} isDark={isDark} canEdit={canEdit} onAddMember={() => openTeamModal()} onEditMember={openTeamModal} onDeleteMember={deleteTeamMember} />
                <ToolsSection tools={scoping.tools} isDark={isDark} canEdit={canEdit} onEdit={openToolsModal} />
                <TasksSection tasks={scoping.tasks} isDark={isDark} canEdit={canEdit} onAddTask={() => openTaskModal()} onEditTask={openTaskModal} onDeleteTask={deleteTask} onToggleTaskStatus={toggleTaskStatus} getStatusColor={getStatusColor} getStatusIcon={getStatusIcon} />

                {/* Continue to Next Step */}
                <div className={clsx('p-4 rounded-lg border flex items-center justify-between', isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200')}>
                    <div>
                        <h3 className={clsx('font-semibold', isDark ? 'text-white' : 'text-slate-800')}>Ready to continue?</h3>
                        <p className="text-sm text-slate-500">Proceed to define your process flow diagram</p>
                    </div>
                    <NextStepButton currentStep={1} isComplete={completedTasks > 0} />
                </div>
            </div>

            {/* ============================================================ */}
            {/* MODALS */}
            {/* ============================================================ */}

            {/* Intent Modal */}
            {activeModal === 'intent' && (
                <Modal title="Edit Project Intent" onClose={() => setActiveModal(null)} onSave={saveIntent}>
                    <div>
                        <label className={labelClasses}>Goals (one per line)</label>
                        <textarea value={intentForm.goals} onChange={e => setIntentForm({ ...intentForm, goals: e.target.value })} className={clsx(inputClasses, 'h-24')} placeholder="Enter goals, one per line" />
                    </div>
                    <div>
                        <label className={labelClasses}>Scope Boundaries</label>
                        <input type="text" value={intentForm.scopeBoundaries} onChange={e => setIntentForm({ ...intentForm, scopeBoundaries: e.target.value })} className={inputClasses} placeholder="Define scope boundaries" />
                    </div>
                    <div>
                        <label className={labelClasses}>Assumptions (one per line)</label>
                        <textarea value={intentForm.assumptions} onChange={e => setIntentForm({ ...intentForm, assumptions: e.target.value })} className={clsx(inputClasses, 'h-24')} placeholder="Enter assumptions, one per line" />
                    </div>
                    <div>
                        <label className={labelClasses}>Exclusions (one per line)</label>
                        <textarea value={intentForm.exclusions} onChange={e => setIntentForm({ ...intentForm, exclusions: e.target.value })} className={clsx(inputClasses, 'h-24')} placeholder="Enter exclusions, one per line" />
                    </div>
                </Modal>
            )}

            {/* Timing Modal */}
            {activeModal === 'timing' && (
                <Modal title="Edit Project Timing" onClose={() => setActiveModal(null)} onSave={() => setActiveModal(null)}>
                    <div>
                        <label className={labelClasses}>Kickoff Date</label>
                        <input type="date" value={formatDateForInput(scoping.timing.kickoffDate)} onChange={e => updateTimingDates(e.target.value, formatDateForInput(scoping.timing.targetCompletionDate))} className={inputClasses} />
                    </div>
                    <div>
                        <label className={labelClasses}>Target Completion Date</label>
                        <input type="date" value={formatDateForInput(scoping.timing.targetCompletionDate)} onChange={e => updateTimingDates(formatDateForInput(scoping.timing.kickoffDate), e.target.value)} className={inputClasses} />
                    </div>
                </Modal>
            )}

            {/* Milestone Modal */}
            {activeModal === 'milestone' && (
                <Modal title={editingIndex !== null ? 'Edit Milestone' : 'Add Milestone'} onClose={() => { setActiveModal(null); setEditingIndex(null); }} onSave={saveMilestone}>
                    <div>
                        <label className={labelClasses}>Milestone Name</label>
                        <input type="text" value={milestoneForm.name} onChange={e => setMilestoneForm({ ...milestoneForm, name: e.target.value })} className={inputClasses} placeholder="Enter milestone name" />
                    </div>
                    <div>
                        <label className={labelClasses}>Due Date</label>
                        <input type="date" value={milestoneForm.date} onChange={e => setMilestoneForm({ ...milestoneForm, date: e.target.value })} className={inputClasses} />
                    </div>
                    <div>
                        <label className={labelClasses}>Status</label>
                        <select value={milestoneForm.status} onChange={e => setMilestoneForm({ ...milestoneForm, status: e.target.value as any })} className={inputClasses}>
                            <option value="pending">Pending</option>
                            <option value="in-progress">In Progress</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>
                </Modal>
            )}

            {/* Team Modal */}
            {activeModal === 'team' && (
                <Modal title={editingIndex !== null ? 'Edit Team Member' : 'Add Team Member'} onClose={() => { setActiveModal(null); setEditingIndex(null); }} onSave={saveTeamMember}>
                    <div>
                        <label className={labelClasses}>Name</label>
                        <input type="text" value={teamMemberForm.name} onChange={e => setTeamMemberForm({ ...teamMemberForm, name: e.target.value })} className={inputClasses} placeholder="Enter name" />
                    </div>
                    <div>
                        <label className={labelClasses}>Role</label>
                        <input type="text" value={teamMemberForm.role} onChange={e => setTeamMemberForm({ ...teamMemberForm, role: e.target.value })} className={inputClasses} placeholder="e.g., Process Engineer" />
                    </div>
                    <div>
                        <label className={labelClasses}>Department</label>
                        <input type="text" value={teamMemberForm.department} onChange={e => setTeamMemberForm({ ...teamMemberForm, department: e.target.value })} className={inputClasses} placeholder="e.g., Manufacturing" />
                    </div>
                    <div>
                        <label className={labelClasses}>Expertise</label>
                        <input type="text" value={teamMemberForm.expertise} onChange={e => setTeamMemberForm({ ...teamMemberForm, expertise: e.target.value })} className={inputClasses} placeholder="e.g., Welding processes" />
                    </div>
                </Modal>
            )}

            {/* Tools Modal */}
            {activeModal === 'tools' && (
                <Modal title="Edit Tools & Resources" onClose={() => setActiveModal(null)} onSave={saveTools}>
                    <div>
                        <label className={labelClasses}>Software (one per line)</label>
                        <textarea value={toolsForm.software} onChange={e => setToolsForm({ ...toolsForm, software: e.target.value })} className={clsx(inputClasses, 'h-20')} placeholder="Enter software tools" />
                    </div>
                    <div>
                        <label className={labelClasses}>Templates (one per line)</label>
                        <textarea value={toolsForm.templates} onChange={e => setToolsForm({ ...toolsForm, templates: e.target.value })} className={clsx(inputClasses, 'h-20')} placeholder="Enter templates" />
                    </div>
                    <div>
                        <label className={labelClasses}>Standards (one per line)</label>
                        <textarea value={toolsForm.standards} onChange={e => setToolsForm({ ...toolsForm, standards: e.target.value })} className={clsx(inputClasses, 'h-20')} placeholder="Enter standards" />
                    </div>
                    <div>
                        <label className={labelClasses}>References (one per line)</label>
                        <textarea value={toolsForm.references} onChange={e => setToolsForm({ ...toolsForm, references: e.target.value })} className={clsx(inputClasses, 'h-20')} placeholder="Enter references" />
                    </div>
                </Modal>
            )}

            {/* Task Modal */}
            {activeModal === 'task' && (
                <Modal title={editingIndex !== null ? 'Edit Task' : 'Add Task'} onClose={() => { setActiveModal(null); setEditingIndex(null); }} onSave={saveTask}>
                    <div>
                        <label className={labelClasses}>Task Name</label>
                        <input type="text" value={taskForm.name} onChange={e => setTaskForm({ ...taskForm, name: e.target.value })} className={inputClasses} placeholder="Enter task name" />
                    </div>
                    <div>
                        <label className={labelClasses}>Owner</label>
                        <select value={taskForm.owner} onChange={e => setTaskForm({ ...taskForm, owner: e.target.value })} className={inputClasses}>
                            <option value="">Select owner...</option>
                            {scoping.team.members.map((member, idx) => <option key={idx} value={member.name}>{member.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className={labelClasses}>Due Date</label>
                        <input type="date" value={taskForm.dueDate} onChange={e => setTaskForm({ ...taskForm, dueDate: e.target.value })} className={inputClasses} />
                    </div>
                    <div>
                        <label className={labelClasses}>Status</label>
                        <select value={taskForm.status} onChange={e => setTaskForm({ ...taskForm, status: e.target.value as any })} className={inputClasses}>
                            <option value="not-started">Not Started</option>
                            <option value="in-progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="blocked">Blocked</option>
                        </select>
                    </div>
                </Modal>
            )}
        </div >
    );
};

export default ProjectScopingPage;
